import { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiException, CloneGroupRequest, Group } from '../../../../types';
import GeneralDialog from '../../../shared/general-dialog';
import { Controller, useForm } from 'react-hook-form';
import {
  UserKeys,
  cloneGroup,
  doApiCheck,
  getDirectoryGroup,
  getGroupLabelsQuery,
} from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import AvatarChip from '../../../shared/avatar-chip';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import { useUserSettings } from '../../../utils/use-user-settings';
import axios from 'axios';
import { Messages } from '../../../utils/message-constants';
import GroupLabelsCheckboxes from '../../../shared/group-labels-checkboxes';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { groupLabels } from '../../../shared/group-labels-config';
import { useNavigate } from 'react-router-dom';
import { pagesConfig } from '../../../shared/pages-config';
import { emailRegex } from '../../../utils/regular-expressions';
import GroupDialogSkeleton from './group-dialog-skeleton';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grow from '@mui/material/Grow';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
interface CloneGroupDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedGroup: Group;
  domains: string[];
}

interface FormInput {
  name: string;
  email: string;
  description: string;
  groupClone: string;
  shouldCloneMembers: boolean;
  shouldCloneSettings: boolean;
  shouldCloneSecuritySettings: boolean;
  labels: { [key: string]: boolean };
}

export default function CloneGroupDialog({
  open,
  handleClose,
  selectedGroup,
  domains,
}: CloneGroupDialogProps) {
  const { createEvent } = useAnalytics();
  const {
    currentTier,
    primaryDomain,
    directoryRoles,
    changeLogRoles,
    features,
  } = useUserSettings();
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [domain, setDomain] = useState(primaryDomain);
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const navigate = useNavigate();

  async function checkIfGroupEmailExists(userEmail: string) {
    try {
      await getDirectoryGroup(userEmail);
      return true;
    } catch {
      return false;
    }
  }

  useEffect(() => {
    const domainToSet = domains.includes(primaryDomain)
      ? primaryDomain
      : domains[0];

    if (domain !== domainToSet) {
      setDomain(domainToSet);
    }
  }, [domains]);

  const defaultValues = {
    name: '',
    email: '',
    description: '',
    groupClone: selectedGroup.email,
    shouldCloneMembers: true,
    shouldCloneSettings: true,
    shouldCloneSecuritySettings: true,
    labels: {
      isMailing: true, //always true
      isSecurity: false,
      isLocked: false,
    },
  };

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    watch,
    setError,
    formState: { errors, isValid, isValidating },
  } = useForm<FormInput>({
    mode: 'onBlur',
    defaultValues: defaultValues,
  });

  const groupClone = watch('groupClone');
  const labelsValue = watch('labels');

  const group = useQuery({
    queryKey: [UserKeys.CloneDropDown, groupClone],
    queryFn: () => getDirectoryGroup(groupClone),
    enabled: groupClone !== '' && !!groupClone,
  });

  const { data: groupData, isLoading: isGroupLoading } = group;

  const mappedGroup: Group = {
    ...groupData,
    directMemberCount: groupData?.directMembersCount ?? 0,
    domain: groupData?.email!.split('@')[1] ?? '',
    email: groupData?.email ?? '',
    id: groupData?.id,
    name: groupData?.name ?? '',
  };

  const { data: accessGranted, isLoading: checkingApiAccess } = useQuery({
    queryKey: [UserKeys.AdminApiSettings, 'cloudIdentity'],
    queryFn: () => doApiCheck('cloudIdentity', null),
    staleTime: 0,
  });

  const isAbleToEditGroupLabels = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.EditGroupLabels.value
  );

  const { isLoading: isLabelLoading, data: groupLabelsData } = useQuery({
    ...getGroupLabelsQuery(mappedGroup.email, mappedGroup.id ?? ''),
    enabled:
      mappedGroup && !!accessGranted && !isGroupLoading && groupClone !== '',
  });

  function handleError(error: unknown) {
    if (axios.isAxiosError(error)) {
      const errorResponse = error.response?.data as ApiException;
      if (errorResponse.message?.includes('Entity already exists.')) {
        setError('email', {
          type: 'manual',
          message: 'Email address is already in use.',
        });
      } else if (
        errorResponse.message?.includes(
          'Cannot allow external members to join group.'
        )
      ) {
        handleErrors({
          error,
          snackbarMessage:
            'Please check the "Who can join group" field in the template or contact a Group administrator.',
        });
      } else if (
        errorResponse.message?.includes(
          'Cannot allow external members to view group content'
        )
      ) {
        handleErrors({
          error,
          snackbarMessage:
            'Please check the "Who can view conversations" field in the template or contact a Group administrator.',
        });
      } else {
        handleErrors({
          error,
          snackbarMessage: Messages.Errors.Network.Cloning('group'),
        });
      }
    } else {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Cloning('group'),
      });
    }
  }

  useEffect(() => {
    if (groupLabelsData) {
      setValue(
        'labels',
        {
          isMailing: true, // This is always true and disabled for new groups.
          isSecurity: Object.prototype.hasOwnProperty.call(
            groupLabelsData,
            'cloudidentity.googleapis.com/groups.security'
          ),
          isLocked: Object.prototype.hasOwnProperty.call(
            groupLabelsData,
            'cloudidentity.googleapis.com/groups.locked'
          ),
        },
        { shouldDirty: true }
      );
    }
  }, [groupLabelsData, setValue]);

  const onSubmit = () => {
    if (!group.data) return;
    if (isAbleToSeeUIElement(changeLogRoles, DirectoryRoles.CloneGroup.value)) {
      setOpenChangeLogDialog(true);
    } else {
      const newLabels: { [key: string]: string } = {};
      const formLabels = getValues('labels');

      groupLabels.forEach((label) => {
        if (formLabels[label.name]) {
          newLabels[label.key] = '';
        }
      });
      mutate({
        sourceGroupEmail: mappedGroup.email,
        sourceGroupId: mappedGroup.id ?? '',
        newGroupEmail: getValues('email') + '@' + domain,
        newGroupName: getValues('name'),
        newGroupDescription: getValues('description'),
        newGroupLabels: newLabels,
        cloneMembers: getValues('shouldCloneMembers'),
        cloneBusinessSettings: getValues('shouldCloneSettings'),
        cloneSecuritySettings: getValues('shouldCloneSecuritySettings'),
        changeLogMessage,
      });
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: CloneGroupRequest) => cloneGroup(data),
    onSuccess: (data) => {
      setChangeLogMessage('');
      if (
        currentTier === 'enterprise' &&
        isFeatureEnabled('indexGroupsReact', features)
      ) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.GroupsIndexedPage],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.Groups],
        });
      }
      queryClient.setQueryData([UserKeys.DirectoryGroup, data.email], {
        ...data,
        directMembersCount: 0,
      });
      navigate(`/${pagesConfig.groups.link}/${data.email}`);
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Cloned('Group'),
      });
    },
    onError: (error) => {
      handleError(error);
    },
    onSettled: () => {
      handleClose();
      reset();
      createEvent(analytics.directory.groups.clone_group, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
    },
  });

  const isLoading = isGroupLoading || isLabelLoading || checkingApiAccess;

  if (isLoading) {
    return (
      <GeneralDialog
        open={open}
        handleClose={handleClose}
        title="Clone Group"
        maxWidth="md"
        dialogContent={<GroupDialogSkeleton />}
      />
    );
  }

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        reset();
        setDomain(domains[0]);
        handleClose();
      }}
      title="Clone Group"
      maxWidth="md"
      dialogContent={
        <Stack sx={{ px: 2 }} gap={2}>
          <Stack>
            <Typography
              variant="caption"
              sx={{ mb: 0.5, color: 'text.secondary' }}
            >
              Source Group
            </Typography>
            <AvatarChip type="Group" email={groupClone} size="medium" />
          </Stack>
          <Stack>
            <Controller
              name={'name'}
              control={control}
              rules={{
                required: 'Name is required.',
              }}
              render={({ field }) => (
                <TextField
                  slotProps={{
                    input: {
                      id: 'name',
                    },
                    inputLabel: {
                      htmlFor: 'name',
                    },
                  }}
                  label="Name"
                  fullWidth
                  autoComplete="off"
                  error={!!errors.name}
                  helperText={
                    errors?.name?.message ||
                    'Enter a name that identifies the group in lists and messages.'
                  }
                  required
                  {...field}
                />
              )}
            />
          </Stack>
          <Stack width="100%">
            <Stack
              flexDirection="row"
              alignItems="baseline"
              width="100%"
              gap={1}
            >
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'Email is required.',
                  validate: {
                    userNameExists: async (value) => {
                      const domain =
                        primaryDomain.length !== 0 ? primaryDomain : domains[0];
                      const userEmail = `${value}@${domain}`;
                      const userExists = await checkIfGroupEmailExists(
                        userEmail
                      );

                      if (userExists) {
                        return 'Group already exists.';
                      }
                    },
                    notValidEmail: (value) => {
                      const domain =
                        primaryDomain.length !== 0 ? primaryDomain : domains[0];
                      const userEmail = `${value}@${domain}`;
                      if (value && !emailRegex.test(userEmail)) {
                        return `${userEmail} is not a valid email address.`;
                      }
                      return true;
                    },
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    slotProps={{
                      input: {
                        id: 'email',
                        endAdornment: isValidating ? (
                          <CircularProgress size={20} />
                        ) : null,
                      },
                      inputLabel: {
                        htmlFor: 'email',
                      },
                    }}
                    label="Email"
                    fullWidth
                    autoComplete="off"
                    error={!!errors.email}
                    required
                  />
                )}
              />
              <Typography>@</Typography>
              <TextField
                label="Domain"
                select
                fullWidth
                value={domain}
                onChange={(event) => setDomain(event.target.value)}
              >
                {domains.map((domain) => (
                  <MenuItem value={domain} key={domain}>
                    {domain}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Typography
              variant="caption"
              color={errors.email ? 'error.main' : 'text.secondary'}
              sx={{ mt: 0.5, ml: 2 }}
            >
              {errors?.email?.message ||
                'Enter an email address for the group.'}
            </Typography>
          </Stack>

          <Stack>
            <Controller
              name={'description'}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  slotProps={{
                    input: {
                      id: 'description',
                    },
                    inputLabel: {
                      htmlFor: 'description',
                    },
                  }}
                  label="Description"
                  helperText="Enter the purpose of the group or how it's used."
                  fullWidth
                  autoComplete="off"
                  sx={{ mt: 1 }}
                />
              )}
            />
          </Stack>

          <Stack
            spacing={2}
            sx={{
              ml: accessGranted ? 1.5 : 7.5,
            }}
          >
            {/* Clone Members */}
            <Controller
              name="shouldCloneMembers"
              control={control}
              render={({ field }) => (
                <FormControl component="fieldset" variant="standard">
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={{ p: 0, mr: 2 }}
                        {...field}
                        checked={field.value}
                      />
                    }
                    label={
                      <Typography fontWeight={500}>Group Members</Typography>
                    }
                  />
                  <FormHelperText sx={{ mt: 0, ml: 3.8 }}>
                    {`Include all members from ${groupClone}`}
                  </FormHelperText>
                </FormControl>
              )}
            />

            {/* Clone Settings */}
            <Controller
              name="shouldCloneSettings"
              control={control}
              render={({ field }) => (
                <FormControl component="fieldset" variant="standard">
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={{ p: 0, mr: 2 }}
                        {...field}
                        checked={field.value}
                      />
                    }
                    label={
                      <Typography fontWeight={500}>
                        Group Business Settings
                      </Typography>
                    }
                  />
                  <FormHelperText sx={{ mt: 0, ml: 3.8 }}>
                    {`Include business settings from ${groupClone}`}
                  </FormHelperText>
                </FormControl>
              )}
            />
            {/* Clone Security Settings Without Alert*/}
            {accessGranted ? (
              <Controller
                name="shouldCloneSecuritySettings"
                control={control}
                render={({ field }) => (
                  <FormControl component="fieldset" variant="standard">
                    <FormControlLabel
                      control={
                        <Checkbox
                          sx={{ p: 0, mr: 2 }}
                          {...field}
                          checked={field.value && accessGranted}
                          disabled={!accessGranted}
                        />
                      }
                      label={
                        <Typography fontWeight={500}>
                          Group Security Settings
                        </Typography>
                      }
                    />
                    <FormHelperText sx={{ mt: 0, ml: 3.8 }}>
                      {`Include security settings from ${groupClone}`}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            ) : null}
          </Stack>

          {/* Clone Security Settings With Alert*/}
          {!accessGranted ? (
            <Alert severity="warning">
              You have not{' '}
              <Link
                href="https://gpanel.io/knowledge/getting-started/enabling-new-scopes"
                target="_blank"
                rel="noopener noreferrer"
              >
                granted data access
              </Link>{' '}
              to the Cloud Identity API for gPanel.{' '}
              <Controller
                name="shouldCloneSecuritySettings"
                control={control}
                render={({ field }) => (
                  <FormControl component="fieldset" variant="standard">
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          sx={{ p: 0, ml: 1.2, mt: 1 }}
                          checked={field.value && accessGranted}
                          disabled={!accessGranted}
                        />
                      }
                      label={
                        <Typography fontWeight={500} sx={{ ml: 1.8, mt: 1 }}>
                          Group Security Settings
                        </Typography>
                      }
                    />
                    <FormHelperText sx={{ mt: 0, ml: 4.8 }}>
                      {`Include security settings from ${groupClone}`}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Alert>
          ) : null}

          <Stack>
            <Stack spacing={2} pb={2} mt={accessGranted === false ? 2 : 0}>
              <Typography>
                Select the labels you want to apply to this group:
              </Typography>
            </Stack>
            <Controller
              name="labels"
              control={control}
              render={({ field }) => (
                <GroupLabelsCheckboxes
                  isLoading={isGroupLoading}
                  checkedStates={labelsValue}
                  onStateChange={(name, checked) => {
                    field.onChange({
                      ...labelsValue,
                      [name]: checked,
                    });
                  }}
                  disabledStates={{
                    isMailing: true,
                    isSecurity: !isAbleToEditGroupLabels || !accessGranted,
                    isLocked: !isAbleToEditGroupLabels || !accessGranted,
                  }}
                  accessGranted={accessGranted || false}
                />
              )}
            />
          </Stack>

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const newLabels: { [key: string]: string } = {};
              const formLabels = getValues('labels');

              groupLabels.forEach((label) => {
                if (formLabels[label.name]) {
                  newLabels[label.key] = '';
                }
              });

              mutate({
                sourceGroupEmail: mappedGroup.email,
                sourceGroupId: mappedGroup.id ?? '',
                newGroupEmail: getValues('email') + '@' + domain,
                newGroupName: getValues('name'),
                newGroupDescription: getValues('description'),
                newGroupLabels: newLabels,
                cloneMembers: getValues('shouldCloneMembers'),
                cloneBusinessSettings: getValues('shouldCloneSettings'),
                cloneSecuritySettings: getValues('shouldCloneSecuritySettings'),
                changeLogMessage,
              });
            }}
            mutateLoading={isPending}
          />

          <Grow in={true}>
            <Alert severity="info">
              The group cloning process can take a few minutes to apply members
              and settings as needed.
            </Alert>
          </Grow>
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit(onSubmit),
        disabled: isPending || !isValid || groupClone.length === 0,
        title: 'Clone',
      }}
    />
  );
}
